import TextField from '@mui/material/TextField';
import { ReactNode, ChangeEvent } from 'react';
import { SxProps, Theme } from '@mui/material';
import { FormFieldWrapper } from '../../layout';
import { INPUT_MODES, INPUT_TYPES, InputType, InputMode } from './textField.constants';
import { filterDecimalInput, filterNumericInput } from './textField.utils';

export interface BaseTextFieldProps {
  id?: string;
  value?: string | number | null;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  type?: InputType;
  inputMode?: InputMode;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  showErrorInline?: boolean;
  reserveErrorSpace?: boolean;
  isMultiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  orientation?: 'row' | 'column';
  prefix?: ReactNode;
  suffix?: ReactNode;
  hideArrow?: boolean;
  inputProps?: Record<string, unknown>;
  sx?: SxProps<Theme>;
  wrapperSx?: SxProps<Theme>;
  labelVariant?: string;
  [key: string]: any;
}

export default function BaseTextField(props: BaseTextFieldProps) {
  const {
    id,
    value,
    onChange,
    onBlur,
    type = INPUT_TYPES.TEXT,
    inputMode = INPUT_MODES.TEXT,
    label,
    placeholder = 'Enter text',
    disabled = false,
    readOnly = false,
    error = false,
    errorMessage,
    helperText,
    showErrorInline = true,
    reserveErrorSpace = false,
    isMultiline = false,
    rows,
    fullWidth = false,
    orientation = 'column',
    prefix,
    suffix,
    hideArrow = false,
    inputProps = {},
    sx = {},
    wrapperSx = {},
    labelVariant,
  } = props;

  const normalizedValue = value ?? '';
  const shouldShowError = showErrorInline && error;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    const next = e.target.value;
    if (inputMode === INPUT_MODES.NUMERIC) {
      onChange(filterNumericInput(next));
      return;
    }
    if (inputMode === INPUT_MODES.DECIMAL) {
      onChange(filterDecimalInput(next));
      return;
    }
    onChange(next);
  };

  return (
    <FormFieldWrapper
      label={label}
      htmlFor={id}
      orientation={orientation}
      error={shouldShowError}
      errorMessage={shouldShowError ? errorMessage : undefined}
      helperText={helperText}
      reserveErrorSpace={reserveErrorSpace}
      sx={wrapperSx}
      labelVariant={labelVariant}
      labelAlignment={isMultiline ? 'start' : undefined}
    >
      <TextField
        id={id}
        value={normalizedValue}
        type={type}
        size='small'
        variant='outlined'
        disabled={disabled}
        placeholder={placeholder}
        fullWidth={fullWidth}
        error={shouldShowError}
        multiline={isMultiline}
        rows={isMultiline ? rows : undefined}
        onChange={handleChange}
        onBlur={onBlur}
        slotProps={{
          input: {
            readOnly,
            startAdornment: prefix,
            endAdornment: suffix,
            sx: { pr: suffix ? 1 : 0, pl: prefix ? 1 : 0, whiteSpace: 'nowrap' },
          },
          htmlInput: inputProps,
          formHelperText: { sx: { margin: 0, textAlign: 'right' } },
        }}
        sx={{
          ...(hideArrow &&
            type === INPUT_TYPES.NUMBER && {
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none' },
              '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none' },
            }),
          ...sx,
        }}
      />
    </FormFieldWrapper>
  );
}
